import {ChartModel} from '@xh/hoist/cmp/chart';
import {HoistModel, LoadSpec, managed, PlainObject, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {CnnFearAndGreedAggregate, FearAndGreedTypeEnum} from '../../../../data/DataTypes';
import {getDefaultOptions} from '../../../../../core/ServiceUtils';

export type FearAndGreedSummary = {
    type: FearAndGreedTypeEnum;
    score: number;
};

export class FearAndGreedWidgetModel extends HoistModel {
    @observable accessor score: number;
    @observableRef accessor scoreDetails: FearAndGreedSummary[] = [];
    @observable @managed accessor summaryChartModel = new ChartModel({
        highchartsConfig: this.createSummaryChartModel()
    });
    @managed indexChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('Historical Fear & Greed Index', null)
    });
    @managed marketMomentumChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('MARKET MOMENTUM', 'S&P 500 and 125-day moving average')
    });
    @managed stockPriceStrengthChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('STOCK PRICE STRENGTH', 'Net new 52-week highs and lows on the NYSE')
    });
    @managed stockPriceBreadthChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('STOCK PRICE BREADTH', 'McClellan Volume Summation Index')
    });
    @managed putCalOptionsChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('PUT and CALL OPTIONS', '5-day average put/call ratio')
    });
    @managed marketVolatilityChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('MARKET VOLATILITY', 'VIX and its 50-day moving average', '%')
    });
    @managed safeHavenDemandChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('SAFE HAVEN DEMAND', 'Difference in 20-day stock and bond returns', '%')
    });
    @managed junkBondDemandChartModel = new ChartModel({
        highchartsConfig: this.createHistoryChartModel('JUNK BOND DEMAND', 'Yield spread: junk bonds vs. investment grade', '%')
    });

    getChartModel(type: FearAndGreedTypeEnum): ChartModel {
        switch (type) {
            case FearAndGreedTypeEnum.Index:
                return this.indexChartModel;
            case FearAndGreedTypeEnum.MarketMomentum:
                return this.marketMomentumChartModel;
            case FearAndGreedTypeEnum.StockPriceStrength:
                return this.stockPriceStrengthChartModel;
            case FearAndGreedTypeEnum.StockPriceBreadth:
                return this.stockPriceBreadthChartModel;
            case FearAndGreedTypeEnum.PutCallOptions:
                return this.putCalOptionsChartModel;
            case FearAndGreedTypeEnum.MarketVolatility:
                return this.marketVolatilityChartModel;
            case FearAndGreedTypeEnum.SafeHavenDemand:
                return this.safeHavenDemandChartModel;
            case FearAndGreedTypeEnum.JunkBondDemand:
                return this.junkBondDemandChartModel;
        }
    }

    override onLinked() {
        super.onLinked();

        this.loadAsync();
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const data: CnnFearAndGreedAggregate = await XH.fetchService.fetchJson({
            url: `${XH.getConf('svcApiUrl')}/economics/fearAndGreed`,
            method: 'GET',
            ...getDefaultOptions()
        });

        runInAction(() => {
            this.score = Math.round(data.fearAndGreed.score);
            XH.safeDestroy(this.summaryChartModel);
            this.summaryChartModel = new ChartModel({
                highchartsConfig: this.createSummaryChartModel()
            });
        });

        this.populateChartData(data);
    }

    @action private populateChartData(data: CnnFearAndGreedAggregate) {
        this.scoreDetails = [
            {type: FearAndGreedTypeEnum.Index, score: this.score},
            {type: FearAndGreedTypeEnum.MarketMomentum, score: data.marketMomentumSp500.score},
            {
                type: FearAndGreedTypeEnum.StockPriceStrength,
                score: data.stockPriceStrength.score
            },
            {
                type: FearAndGreedTypeEnum.StockPriceBreadth,
                score: data.stockPriceBreadth.score
            },
            {type: FearAndGreedTypeEnum.PutCallOptions, score: data.putCallOptions.score},
            {
                type: FearAndGreedTypeEnum.MarketVolatility,
                score: data.marketVolatilityVix.score
            },
            {type: FearAndGreedTypeEnum.SafeHavenDemand, score: data.safeHavenDemand.score},
            {type: FearAndGreedTypeEnum.JunkBondDemand, score: data.junkBondDemand.score}
        ];

        this.summaryChartModel.setSeries([
            {
                name: 'summary',
                data: [{y: this.score}],
                dataLabels: {
                    y: -25,
                    zIndex: 6,
                    borderRadius: 20,
                    borderColor: 'gray',
                    padding: 8,
                    backgroundColor: 'var(--xh-bg)',
                    style: {fontSize: '1.2em', color: 'gray'}
                }
            }
        ]);

        this.indexChartModel.setSeries([{name: 'Index', type: 'line', data: data.fearAndGreedHistorical.data}]);

        this.marketMomentumChartModel.setSeries([
            {name: 'S&P 500', type: 'line', data: data.marketMomentumSp500.data},
            {
                name: 'SMA (125D)',
                type: 'line',
                color: 'orange',
                dashStyle: 'shortdot',
                data: data.marketMomentumSp125.data
            }
        ]);

        this.stockPriceStrengthChartModel.setSeries([{name: 'Strength', type: 'line', data: data.stockPriceStrength.data}]);

        this.stockPriceBreadthChartModel.setSeries([{name: 'Breadth', type: 'line', data: data.stockPriceBreadth.data}]);

        this.putCalOptionsChartModel.setSeries([{name: 'Put/Call', type: 'line', data: data.putCallOptions.data}]);

        this.marketVolatilityChartModel.setSeries([
            {name: 'VIX', type: 'line', data: data.marketVolatilityVix.data},
            {
                name: 'SMA (5D)',
                type: 'line',
                color: 'orange',
                dashStyle: 'shortdot',
                data: data.marketVolatilityVix50.data
            }
        ]);

        this.safeHavenDemandChartModel.setSeries([{name: 'Demand', type: 'line', data: data.safeHavenDemand.data}]);

        this.junkBondDemandChartModel.setSeries([{name: 'Demand', type: 'line', data: data.junkBondDemand.data}]);
    }

    private createSummaryChartModel(): PlainObject {
        const thickness = 50;
        const defaultBg = 'var(--fg-default-segment-color)';
        const labelColor = 'var(--xh-text-color)';

        return {
            chart: {
                type: 'gauge',
                height: '100%',
                margin: 0,
                plotBackgroundColor: null,
                plotBackgroundImage: null,
                plotBorderWidth: 0,
                plotShadow: false
            },

            legeng: {enabled: false},

            pane: {
                startAngle: -90,
                endAngle: 90,
                background: null,
                center: ['50%', '80%'],
                size: '120%'
            },

            yAxis: [
                {
                    min: 0,
                    max: 100,
                    minorTickInterval: null,
                    tickInterval: 25,
                    offset: -60,
                    plotBands: null,
                    labels: {
                        distance: -15,
                        rotation: 'auto'
                    }
                },
                {
                    min: 0,
                    max: 100,
                    labels: {enabled: false},
                    minorTickInterval: null,
                    tickInterval: 25,
                    tickPositions: [0, 25, 45, 55, 75, 100],
                    tickColor: 'var(--xh-bg)',
                    tickLength: thickness,
                    tickWidth: 2,
                    offset: 0,
                    plotBands: [
                        {
                            from: 0,
                            to: 25,
                            thickness,
                            label: {
                                text: 'EXTREME FEAR',
                                align: 'right',
                                rotation: -70,
                                x: 30,
                                y: -5,
                                style: {
                                    color: labelColor,
                                    textOverflow: 'none'
                                }
                            },
                            color: this.score < 25 ? '#c72c41' : defaultBg
                        },
                        {
                            from: 25,
                            to: 45,
                            thickness,
                            label: {
                                text: 'FEAR',
                                align: 'right',
                                rotation: -30,
                                x: 25,
                                y: 30,
                                style: {color: labelColor}
                            },
                            color: this.score > 25 && this.score < 45 ? '#421414' : defaultBg
                        },
                        {
                            from: 45,
                            to: 55,
                            thickness,
                            label: {
                                text: '\u2696',
                                x: -10,
                                y: 42,
                                style: {color: labelColor, fontSize: '1.5em'}
                            },
                            color: this.score > 45 && this.score < 55 ? '#574e24' : defaultBg
                        },
                        {
                            from: 55,
                            to: 75,
                            thickness,
                            label: {
                                text: 'GREED',
                                rotation: 30,
                                x: -22,
                                y: 30,
                                style: {color: labelColor}
                            },
                            color: this.score > 55 && this.score < 75 ? '#103619' : defaultBg
                        },
                        {
                            from: 75,
                            to: 100,
                            thickness,
                            label: {
                                text: 'EXTREME GREED',
                                rotation: 70,
                                x: -30,
                                y: -5,
                                style: {
                                    color: labelColor,
                                    textOverflow: 'none'
                                }
                            },
                            color: this.score > 75 ? '#008b21' : defaultBg
                        }
                    ]
                }
            ],

            tooltip: {enabled: false},

            plotOptions: {
                gauge: {
                    dial: {
                        radius: '80%',
                        backgroundColor: 'gray',
                        baseWidth: 12,
                        topWidth: 2,
                        baseLength: '0%',
                        rearLength: '0%'
                    },
                    pivot: {radius: 6, backgroundColor: 'gray'}
                }
            }
        };
    }

    private createHistoryChartModel(title: string, subtitle: string, format: '$' | '%' | '#' = '#'): PlainObject {
        let labelFormat = null;
        if (format == '%') labelFormat = '{value:.0f}%';

        return {
            chart: {animation: false},
            xAxis: {type: 'datetime', tickPosition: 'inside', crosshair: {dashStyle: 'shortdot'}},
            yAxis: {
                tickPosition: 'inside',
                title: {text: null},
                labels: {
                    format: labelFormat
                }
            },
            legend: {enabled: false},
            title: {text: title, style: {fontSize: '1em'}},
            subtitle: {text: subtitle},
            plotOptions: {
                series: {animation: false},
                line: {
                    marker: {enabled: false},
                    states: {
                        hover: {enabled: false}
                    }
                }
            },
            tooltip: {
                shared: true,
                valueDecimals: 2,
                valuePrefix: format == '$' ? '$' : null,
                valueSuffix: format == '%' ? '%' : null,
                xDateFormat: '%Y-%m-%d',
                pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>'
            }
        };
    }
}
